import { IndexedDBStorage } from '../../../libs/helpers/indexdb'
import { readAllLists, applyOperations } from '../wishlist/wishlistRepository.mjs'
import { createScopedProfile } from '../wishlist/omneoProfileScope.mjs'
import { defaultCreateClient } from '../wishlist/omneoClientFactory.js'

const STORAGE_KEY = 'omneo-wishlists'
const storage = new IndexedDBStorage('omneo-db', 'wishlists')

/** In-flight uncached read, shared so concurrent callers cannot double-create child lists. */
let inFlightRead = null

/**
 * The composition point: the pure repository core never imports the browser-side
 * client, so it is bound here, at the one place both halves are in scope.
 */
export const createProfile = (id) => createScopedProfile(id, defaultCreateClient)

export const invalidateWishlistCache = async () => {
  try {
    await storage.init()
    await storage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error('Failed to invalidate cached wishlists:', error)
  }
}

/**
 * Cache-only read, for callers that want a hint without paying for a fetch.
 * A full read creates a wishlist for any child lacking one, so it is not free.
 */
export const readCachedWishlists = async () => {
  try {
    await storage.init()
    const cached = await storage.getItem(STORAGE_KEY)
    return Array.isArray(cached) ? cached : null
  } catch (error) {
    console.error('Failed to read cached wishlists:', error)
    return null
  }
}

/**
 * Grabs and combines both parent and child wishlists
 * @param {Object} omneoClient - The Omneo client instance
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Array>}
 * @throws {Error} If initialization fails or client is invalid
 */
export const getAllWishlists = async (omneoClient, useCache = false) => {
  if (!omneoClient) {
    throw new Error('Omneo client is required')
  }

  try {
    await storage.init()
  } catch (error) {
    console.error('Failed to initialize storage:', error)
    // Continue without cache if storage fails
  }

  if (useCache) {
    try {
      const cachedData = await storage.getItem(STORAGE_KEY)
      if (cachedData && Array.isArray(cachedData)) {
        return cachedData
      }
    } catch (error) {
      console.error('Failed to retrieve cached wishlists:', error)
      // Continue without cache
    }
  }

  // Deduped: readAllLists CREATES a wishlist for any child lacking one, so two concurrent
  // uncached readers (the drawer and the header count both react to the same update event)
  // would each see none and each create one.
  if (!inFlightRead) {
    inFlightRead = readAllLists({ profileClient: omneoClient.profileClient, createProfile })
      .finally(() => {
        inFlightRead = null
      })
  }
  const wishlists = await inFlightRead

  try {
    await storage.setItem(STORAGE_KEY, wishlists)
  } catch (error) {
    console.error('Failed to cache wishlists:', error)
  }

  return wishlists
}

const findMatchingItem = (wishlist, metaKey, matchValue) => {
  if (!wishlist || !Array.isArray(wishlist.items)) return null
  return wishlist.items.find((item) => String(item?.meta?.[metaKey]) === String(matchValue)) ?? null
}

/** Child-list removals need connectionId so applyOperations scopes them via a fresh profile. */
const buildRemoveOperations = (wishlists, metaKey, matchValue) =>
  wishlists.reduce((operations, wishlist) => {
    const item = findMatchingItem(wishlist, metaKey, matchValue)
    if (item) {
      operations.push({ type: 'remove', listId: wishlist.id, itemId: item.id, connectionId: wishlist.profile?.id })
    }
    return operations
  }, [])

const removeItemsFromAllWishlists = async (omneoClient, wishlists, metaKey, matchValue) => {
  const operations = buildRemoveOperations(wishlists, metaKey, matchValue)
  await applyOperations({ profileClient: omneoClient.profileClient, createProfile, operations })
  await invalidateWishlistCache()
}

/**
 * Removes a product from all wishlists
 * @param {Object} omneoClient - The Omneo client instance
 * @param {Array} wishlists - Array of wishlists to process
 * @param {string|number} productId - ID of the product to remove
 * @throws {Error} If client is invalid
 */
export const removeProductFromAllWishlists = async (omneoClient, wishlists, productId) => {
  if (!omneoClient) {
    throw new Error('Omneo client is required')
  }
  if (!Array.isArray(wishlists)) {
    throw new Error('Wishlists must be an array')
  }
  if (!productId) {
    throw new Error('Product ID is required')
  }

  await removeItemsFromAllWishlists(omneoClient, wishlists, 'productId', productId)
}

/**
 * Removes a variant from all wishlists
 * @param {Object} omneoClient - The Omneo client instance
 * @param {Array} wishlists - Array of wishlists to process
 * @param {string|number} variantId - ID of the variant to remove
 * @throws {Error} If client is invalid
 */
export const removeVariantFromAllWishlists = async (omneoClient, wishlists, variantId) => {
  if (!omneoClient) {
    throw new Error('Omneo client is required')
  }
  if (!Array.isArray(wishlists)) {
    throw new Error('Wishlists must be an array')
  }
  if (!variantId) {
    throw new Error('Variant ID is required')
  }

  await removeItemsFromAllWishlists(omneoClient, wishlists, 'variantId', variantId)
}
